import React, { useState, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown, Users, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, ChevronRight, Eye, Flag, MessageCircle, Award, Activity, Target, Settings } from "lucide-react";
import { Card, SH, Badge, fmtM, fmtX, SetupBanner, resolveF, dealUrl } from "../shared/constants";

const COACHING_SIGNALS = [
  { key: "attainment", label: "Quota Attainment", weight: 3,
    tip: "YTD closed-won bookings ÷ annual quota. Weighted most heavily — it's the ultimate outcome metric. 100% = on pace to hit quota." },
  { key: "pipeline",   label: "Pipeline Coverage", weight: 2,
    tip: "Open pipeline value ÷ remaining quota. Best practice is 3x coverage minimum. Below 2x is a serious risk signal for this rep." },
  { key: "winRate",    label: "Win Rate", weight: 2,
    tip: "Closed-won deals ÷ (won + lost). Compared against your configured win rate target. Low win rate = qualification or competitive issues." },
  { key: "activity",   label: "Deal Activity", weight: 1,
    tip: "% of open deals with activity logged in the last 14 days. Stale deals are a leading indicator of slippage. Score drops for every deal with no recent touch." },
  { key: "dealSize",   label: "Avg Deal Size", weight: 1,
    tip: "Average closed-won deal value this fiscal year vs. your target avg deal size. Consistently low deal size may indicate discounting or under-qualification." },
  { key: "velocity",   label: "Sales Velocity", weight: 1,
    tip: "Target sales cycle ÷ actual avg days from create to close. Score of 100% means this rep closes at or faster than target. Higher is better." },
];

// ── Reusable Tooltip ──
const Tip = ({ text, children }) => {
  const [show, setShow] = React.useState(false);
  const ref = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const handleEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setShow(true);
  };
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: "fixed", zIndex: 9999,
          top: pos.top, left: Math.min(pos.left, window.innerWidth - 280),
          width: 260, background: "#F0F0F0", border: "1px solid rgba(204,26,26,0.10)",
          borderRadius: 8, padding: "10px 12px", boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          fontSize: 11, color: "#666666", lineHeight: 1.6, pointerEvents: "none",
        }}>{text}</div>
      )}
    </span>
  );
};

const InfoIcon = ({ tip }) => (
  <Tip text={tip}>
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 14, height: 14, borderRadius: "50%", border: "1px solid rgba(139,155,190,0.4)",
      color: "#555555", fontSize: 9, fontWeight: 800, cursor: "help", marginLeft: 5,
      flexShrink: 0, lineHeight: 1 }}>?</span>
  </Tip>
);

const RepCoaching = ({ settings, hsData, onGo, userId }) => {
  const [selected, setSelected] = React.useState(null);
  const [notes, setNotes]       = React.useState(() => { try { return JSON.parse(localStorage.getItem("rx_coaching_notes") || "{}"); } catch { return {}; } });
  const [noteInput, setNoteInput] = React.useState("");
  const [tab, setTab]           = React.useState("overview");
  const [showReview, setShowReview] = React.useState(false);
  // Coaching sessions: { [repOwnerId]: [{ id, type, label, createdAt, metrics, review, notes }] }
  const [sessions, setSessions] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("rx_coaching_sessions") || "{}"); } catch { return {}; }
  });

  const metrics   = hsData?.metrics;
  const rawDeals  = hsData?.rawDeals  || [];
  const rawWon    = hsData?.rawWonDeals || [];
  const owners    = hsData?.owners    || [];
  const reps      = (settings.team?.reps || []).filter(r => r.name && r.hubspotOwnerId);

  const fm = settings?.fieldMap || {};
  const F = {
    amount:      fm.amount       || "amount",
    closedate:   fm.closedate    || "closedate",
    createdate:  fm.createdate   || "createdate",
    dealstage:   fm.dealstage    || "dealstage",
    owner:       fm.owner        || "hubspot_owner_id",
    dealname:    fm.dealname     || "dealname",
    lastActivity:fm.lastActivity || "notes_last_activity_date",
    lastModified:fm.lastModified || "hs_lastmodifieddate",
    probability: fm.probability  || "hs_deal_stage_probability",
    nextStep:    fm.nextStep     || "hs_next_step",
    numContacts: fm.numContacts  || "num_associated_contacts",
  };

  const mappings = settings.stages?.mappings || [];
  const wonIds   = mappings.filter(m => m.type === "won").map(m => m.id.toLowerCase());
  const lostIds  = mappings.filter(m => m.type === "lost").map(m => m.id.toLowerCase());
  const isWon    = s => wonIds.length  ? wonIds.includes(s?.toLowerCase())  : s?.toLowerCase() === "closedwon";
  const isLost   = s => lostIds.length ? lostIds.includes(s?.toLowerCase()) : s?.toLowerCase() === "closedlost";
  const isOpen   = s => !isWon(s) && !isLost(s);

  const now = Date.now();
  const fyStart = new Date(`${+settings.company?.fiscalYear || new Date().getFullYear()}-01-01`).getTime();

  // Build per-rep stats
  const repStats = reps.map(rep => {
    const oid = rep.hubspotOwnerId;
    const quota = +rep.quota || 0;

    const myDeals   = rawDeals.filter(d => String(d.properties[F.owner]) === String(oid));
    const myWon     = myDeals.filter(d => isWon(d.properties[F.dealstage]));
    const myLost    = myDeals.filter(d => isLost(d.properties[F.dealstage]));
    const myOpen    = myDeals.filter(d => isOpen(d.properties[F.dealstage]));

    const ytdWon    = myWon.filter(d => {
      const cd = d.properties[F.closedate] ? new Date(d.properties[F.closedate]).getTime() : 0;
      return cd >= fyStart;
    });

    const bookings  = ytdWon.reduce((a, d) => a + (+d.properties[F.amount] || 0), 0);
    const pipeline  = myOpen.reduce((a, d) => a + (+d.properties[F.amount] || 0), 0);
    const attPct    = quota > 0 ? bookings / quota : 0;
    const covX      = quota > 0 ? pipeline / quota : 0;
    const winRate   = (myWon.length + myLost.length) > 0 ? myWon.length / (myWon.length + myLost.length) : 0;
    const avgDeal   = myWon.length > 0 ? bookings / myWon.length : 0;

    // Avg sales cycle (days)
    const cycleDeals = ytdWon.filter(d => d.properties[F.createdate] && d.properties[F.closedate]);
    const avgCycle   = cycleDeals.length > 0
      ? cycleDeals.reduce((a, d) => a + (new Date(d.properties[F.closedate]) - new Date(d.properties[F.createdate])) / 86400000, 0) / cycleDeals.length
      : 0;

    // ── Targets (must be declared before signal calcs that reference them) ───
    const repTeam    = rep.team || rep.segment || "";
    const byTeam     = settings.targets?.byTeam || {};
    const teamTargets= byTeam[repTeam] || {};
    const dealTypes  = settings.targets?.dealTypes?.[repTeam] || ["New Logo","Upsell","Renewal","Cross-sell"];
    const blendTarget = (field, fallback) => {
      const vals = dealTypes.map(dt => +(teamTargets[dt]?.[field] || 0)).filter(v => v > 0);
      return vals.length > 0 ? vals.reduce((a,v) => a+v, 0) / vals.length : fallback;
    };
    const targetWR  = blendTarget("winRate",    +(settings.targets?.winRate)    || 25) / 100;
    const targetDS  = blendTarget("avgDealSize", +(settings.targets?.avgDealSize) || 0);
    const targetCyc = blendTarget("salesCycle",  +(settings.targets?.salesCycle)  || 60);

    // ── Activity signals ────────────────────────────────────────────────────
    const lastTouchMs = d => {
      const la = d.properties[F.lastActivity];
      const lm = d.properties[F.lastModified];
      return la ? new Date(la).getTime() : lm ? new Date(lm).getTime() : 0;
    };

    // Stale 14d (existing) and 7d (new — tighter flag)
    const staleCount  = myOpen.filter(d => { const t = lastTouchMs(d); return !t || (now - t) > 14 * 86400000; }).length;
    const stale7Count = myOpen.filter(d => { const t = lastTouchMs(d); return !t || (now - t) > 7  * 86400000; }).length;

    // No opps created in last 30 days
    const newPipe30 = myOpen.filter(d => {
      const cd = d.properties[F.createdate];
      return cd && (now - new Date(cd).getTime()) < 30 * 86400000;
    });
    const newPipe30Val = newPipe30.reduce((a, d) => a + (+d.properties[F.amount] || 0), 0);

    // ── Next Step hygiene ───────────────────────────────────────────────────
    // Flag open deals where next step is blank OR last activity > 7d
    const noNextStep = myOpen.filter(d => {
      const ns = (d.properties[F.nextStep] || "").trim();
      return ns === "";
    });
    const nextStepLag = myOpen.filter(d => {
      const t = lastTouchMs(d);
      return t > 0 && (now - t) > 7 * 86400000;
    });

    // ── Multi-thread depth ──────────────────────────────────────────────────
    // avg contacts on open deals; flag deals > $50K with only 1 contact
    const dealsWithContacts = myOpen.filter(d => d.properties[F.numContacts] != null);
    const avgContacts = dealsWithContacts.length > 0
      ? dealsWithContacts.reduce((a, d) => a + (+d.properties[F.numContacts] || 0), 0) / dealsWithContacts.length
      : null;
    const singleThreaded = myOpen.filter(d =>
      (+d.properties[F.amount] || 0) >= 50000 &&
      (+d.properties[F.numContacts] || 0) <= 1
    );

    // ── Stage-to-Age proxy ──────────────────────────────────────────────────
    // Can't get exact stage entry date, so flag: open deals where createdate
    // is > 1.5x the target sales cycle old AND deal hasn't moved (stale)
    const stuckDeals = myOpen.filter(d => {
      const ageMs  = d.properties[F.createdate] ? now - new Date(d.properties[F.createdate]).getTime() : 0;
      const ageDays = ageMs / 86400000;
      const t       = lastTouchMs(d);
      const staleDays = t ? (now - t) / 86400000 : 999;
      return ageDays > targetCyc * 1.5 && staleDays > 14;
    });

    // ── Gap-to-throughput ───────────────────────────────────────────────────
    // Can pipeline × rep win rate mathematically cover remaining quota?
    const remaining     = Math.max(0, quota - bookings);
    const forecastable  = pipeline * (winRate || targetWR);
    const throughputGap = remaining > 0 ? forecastable - remaining : 0;
    // Is pipeline enough to hit monthly pace?
    const monthsLeft    = Math.max(1, 12 - new Date().getMonth());
    const monthlyNeeded = remaining / monthsLeft;
    const monthlyForecast = forecastable / monthsLeft;

    // ── 90-Day Cliff ────────────────────────────────────────────────────────
    // Quota "due" in next 90 days (proportional) vs pipe created last 30 days
    const quota90 = quota * (90 / 365);
    const cliff   = quota90 - newPipe30Val; // positive = danger

    const scores = {
      attainment: Math.min(1, attPct),
      pipeline:   Math.min(1, covX / 3),
      winRate:    targetWR > 0 ? Math.min(1, winRate / targetWR) : 0.5,
      activity:   myOpen.length > 0 ? Math.max(0, 1 - staleCount / myOpen.length) : 1,
      dealSize:   targetDS > 0 ? Math.min(1, avgDeal / targetDS) : 0.5,
      velocity:   targetCyc > 0 ? Math.min(1, targetCyc / Math.max(1, avgCycle)) : 0.5,
    };

    const totalWeight = COACHING_SIGNALS.reduce((a, s) => a + s.weight, 0);
    const health = Math.round(
      COACHING_SIGNALS.reduce((a, s) => a + (scores[s.key] || 0) * s.weight, 0) / totalWeight * 100
    );

    // ── Coaching flags ──────────────────────────────────────────────────────
    const flags = [];

    // Quota & attainment
    if (attPct < 0.5)
      flags.push({ label: "Below 50% attainment", severity: "critical", area: "quota",
        detail: `${fmtM(bookings)} booked vs ${fmtM(quota)} quota. Needs ${fmtM(remaining)} more to hit annual number.` });

    // Pipeline coverage
    if (covX < 2)
      flags.push({ label: "Pipeline coverage below 2x", severity: "high", area: "pipeline",
        detail: `${covX.toFixed(1)}x coverage. At current win rate, pipeline forecasts ${fmtM(forecastable)} — ${throughputGap < 0 ? fmtM(Math.abs(throughputGap)) + " short" : "enough to cover"} of remaining ${fmtM(remaining)}.` });

    // Throughput gap — even if coverage > 2x, math might not work
    if (covX >= 2 && throughputGap < 0 && remaining > 0)
      flags.push({ label: "Pipeline can't cover remaining quota", severity: "high", area: "throughput",
        detail: `Pipeline × win rate forecasts ${fmtM(forecastable)} but ${fmtM(remaining)} still needed. Either win rate needs to improve or pipeline needs to grow.` });

    // Win rate
    if (winRate < targetWR * 0.75 && (myWon.length + myLost.length) > 3)
      flags.push({ label: "Win rate significantly below target", severity: "high", area: "winRate",
        detail: `${(winRate * 100).toFixed(0)}% vs ${(targetWR * 100).toFixed(0)}% target. Review last 3 losses for patterns.` });

    // 7-day activity
    if (stale7Count > 1)
      flags.push({ label: `${stale7Count} deals with no activity in 7 days`, severity: stale7Count > 3 ? "high" : "medium", area: "activity7",
        detail: `${stale7Count} open deals untouched for 7+ days. Every quiet deal is a deal going cold.` });
    else if (staleCount > 2)
      flags.push({ label: `${staleCount} deals with no recent activity`, severity: "medium", area: "activity",
        detail: `${staleCount} open deals with no logged activity in 14+ days.` });

    // No new opps in 30 days
    if (newPipe30.length === 0 && quota > 0)
      flags.push({ label: "No new opportunities created in 30 days", severity: "high", area: "newpipe",
        detail: `Zero new deals sourced this month. 90-day quota exposure: ${fmtM(quota90)}. The pipeline clock is running.` });
    else if (cliff > quota90 * 0.5)
      flags.push({ label: "90-Day Cliff risk", severity: "medium", area: "cliff",
        detail: `Only ${fmtM(newPipe30Val)} created in last 30 days vs ${fmtM(quota90)} quota maturing in 90 days. Not enough new pipe to replace what's closing.` });

    // Next step missing
    if (noNextStep.length > 0)
      flags.push({ label: `${noNextStep.length} open deals missing next step`, severity: "medium", area: "nextstep",
        detail: `${noNextStep.length} deal${noNextStep.length > 1 ? "s have" : " has"} no next step logged in HubSpot. A rep working a plan always has a defined next move.` });

    // Single-threaded enterprise deals
    if (singleThreaded.length > 0)
      flags.push({ label: `${singleThreaded.length} large deal${singleThreaded.length > 1 ? "s" : ""} single-threaded`, severity: "medium", area: "multithread",
        detail: `${singleThreaded.length} deal${singleThreaded.length > 1 ? "s" : ""} over $50K with only 1 contact. Single-threaded enterprise deals have a high slip rate.` });

    // Stage-age stall proxy
    if (stuckDeals.length > 0)
      flags.push({ label: `${stuckDeals.length} deal${stuckDeals.length > 1 ? "s" : ""} stalled past expected cycle`, severity: "medium", area: "stageage",
        detail: `${stuckDeals.length} open deal${stuckDeals.length > 1 ? "s are" : " is"} older than ${Math.round(targetCyc * 1.5)} days with no recent activity — likely stuck, not progressing.` });

    // Velocity
    if (avgCycle > targetCyc * 1.4 && cycleDeals.length > 1)
      flags.push({ label: "Sales cycle running long", severity: "medium", area: "velocity",
        detail: `Avg ${Math.round(avgCycle)}d vs ${Math.round(targetCyc)}d target. Identify where deals stall — typically evaluation or procurement.` });

    // Deal size
    if (avgDeal < targetDS * 0.6 && targetDS > 0 && myWon.length > 2)
      flags.push({ label: "Avg deal size below target", severity: "medium", area: "dealSize",
        detail: `${fmtM(avgDeal)} vs ${fmtM(targetDS)} target. Check for discounting or narrow ICP qualification.` });

    return {
      rep, oid, quota, bookings, pipeline, attPct, covX,
      winRate, avgDeal, avgCycle, staleCount, stale7Count, health, flags, scores,
      wonCount: myWon.length, lostCount: myLost.length, openCount: myOpen.length,
      openDeals: myOpen.sort((a, b) => (+b.properties[F.amount] || 0) - (+a.properties[F.amount] || 0)),
      // new signals
      remaining, forecastable, throughputGap, monthlyNeeded, monthlyForecast,
      newPipe30Val, newPipe30Count: newPipe30.length, cliff, quota90,
      noNextStepCount: noNextStep.length, nextStepLagCount: nextStepLag.length,
      singleThreaded, stuckDeals, avgContacts,
    };
  }).sort((a, b) => b.health - a.health);

  const saveNote = (repId) => {
    if (!noteInput.trim()) return;
    const prev = notes[repId] || [];
    const next = { ...notes, [repId]: [...prev, { text: noteInput.trim(), at: new Date().toISOString() }] };
    setNotes(next);
    localStorage.setItem("rx_coaching_notes", JSON.stringify(next));
    setNoteInput("");
  };

  // Save a completed insights review as a coaching session with a metric snapshot
  const saveSession = React.useCallback((repOwnerId, reviewType, reviewData, statsSnapshot) => {
    const session = {
      id:        `${repOwnerId}_${Date.now()}`,
      type:      reviewType.id,
      label:     reviewType.label,
      icon:      reviewType.icon,
      createdAt: new Date().toISOString(),
      metrics: {
        attPct:        statsSnapshot.attPct,
        covX:          statsSnapshot.covX,
        winRate:       statsSnapshot.winRate,
        bookings:      statsSnapshot.bookings,
        pipeline:      statsSnapshot.pipeline,
        health:        statsSnapshot.health,
        activityScore: statsSnapshot.scores?.activity != null
                         ? Math.round(statsSnapshot.scores.activity * 100)
                         : null,
        flagCount:     statsSnapshot.flags?.length || 0,
        // Store full flag list so history shows exactly what was flagged
        flags:         (statsSnapshot.flags || []).map(f => ({
          label:    f.label,
          severity: f.severity,
          detail:   f.detail || null,
        })),
      },
      review: reviewData,
      notes:  [],
    };
    setSessions(prev => {
      const repSessions = [session, ...(prev[repOwnerId] || [])];
      const next = { ...prev, [repOwnerId]: repSessions };
      localStorage.setItem("rx_coaching_sessions", JSON.stringify(next));
      return next;
    });
  }, [userId]);

  // Add a note to an existing session
  const addSessionNote = (repOwnerId, sessionId, text) => {
    if (!text.trim()) return;
    setSessions(prev => {
      const repSessions = (prev[repOwnerId] || []).map(s =>
        s.id === sessionId ? { ...s, notes: [...(s.notes||[]), { text: text.trim(), at: new Date().toISOString() }] } : s
      );
      const next = { ...prev, [repOwnerId]: repSessions };
      localStorage.setItem("rx_coaching_sessions", JSON.stringify(next));
      return next;
    });
  };

  const healthColor = h => h >= 75 ? "#16A34A" : h >= 50 ? "#D97706" : "#DC2626";
  const sevColor    = s => s === "critical" ? "#DC2626" : s === "high" ? "#C2610C" : "#D97706";

  // ── Team grouping for card view ─────────────────────────────────────────
  const teamNames = [...new Set(reps.map(r => r.team || r.segment || "Team").filter(Boolean))];
  const teamGroups = teamNames.map((team, ti) => {
    const members = repStats.filter(r => (r.rep.team || r.rep.segment || "Team") === team);
    const color   = ["#CC1A1A","#D97706","#16A34A","#7C3AED","#0284C7","#DB2777"][ti % 6];
    return { team, members, color };
  });
  const ungrouped = repStats.filter(r => !r.rep.team && !r.rep.segment);
  if (ungrouped.length) teamGroups.push({ team: "All Reps", members: ungrouped, color: "#888" });

  // All teams collapsed by default — click to expand
  const [expandedTeams, setExpandedTeams] = React.useState({});
  const toggleTeam = team => setExpandedTeams(prev => ({ ...prev, [team]: !prev[team] }));

  const selStats = selected ? repStats.find(r => r.oid === selected) : null;

  if (!settings.team?.reps?.some(r => r.name)) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111111", marginBottom: 6 }}>No reps configured yet</div>
        <div style={{ fontSize: 12, color: "#555555", marginBottom: 20 }}>Add your team in Settings to unlock rep coaching.</div>
        <button onClick={onGo} style={{ padding: "9px 20px", background: "rgba(204,26,26,0.10)", border: "1px solid rgba(204,26,26,0.10)", borderRadius: 8, color: "#CC1A1A", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Go to Settings</button>
      </div>
    );
  }

  return (
    <>
    <div style={{ maxWidth: 1140, margin: "0 auto", paddingBottom: 60 }}>

      {/* ── NO REP SELECTED: Team accordion ─────────────────────────────── */}
      {!selStats && (
        <div>
          {teamGroups.map(({ team, members, color }) => {
            const isOpen    = !!expandedTeams[team];
            const critCount = members.filter(r => r.flags.some(f => f.severity === "critical")).length;
            const highCount = members.filter(r => r.flags.some(f => f.severity === "high") && !r.flags.some(f => f.severity === "critical")).length;
            return (
            <div key={team} style={{ marginBottom: 8 }}>
              {/* Clickable team header */}
              <div onClick={() => toggleTeam(team)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px",
                  background:"#FFF", borderRadius: isOpen ? "10px 10px 0 0" : 10,
                  border:`1px solid ${isOpen ? color+"40" : "rgba(0,0,0,0.08)"}`,
                  borderBottom: isOpen ? `1px solid ${color}30` : undefined,
                  cursor:"pointer", transition:"all 0.15s", userSelect:"none" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = color+"50"}
                onMouseLeave={e => e.currentTarget.style.borderColor = isOpen ? color+"40" : "rgba(0,0,0,0.08)"}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:color, flexShrink:0 }}/>
                <span style={{ fontSize:12, fontWeight:800, color:"#111", textTransform:"uppercase", letterSpacing:"0.07em", flex:1 }}>{team}</span>
                <span style={{ fontSize:11, color:"#999", marginRight:4 }}>{members.length} rep{members.length !== 1 ? "s" : ""}</span>
                {critCount > 0 && <span style={{ fontSize:10, fontWeight:700, color:"#DC2626", background:"rgba(220,38,38,0.08)", borderRadius:5, padding:"2px 7px" }}>{critCount} critical</span>}
                {highCount > 0 && <span style={{ fontSize:10, fontWeight:700, color:"#C2610C", background:"rgba(194,97,12,0.08)", borderRadius:5, padding:"2px 7px" }}>{highCount} high</span>}
                <span style={{ fontSize:16, color:"#aaa", marginLeft:4, lineHeight:1 }}>{isOpen ? "▾" : "▸"}</span>
              </div>
              {/* Rep alert cards — shown when expanded */}
              {isOpen && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:12,
                padding:16, background:"rgba(0,0,0,0.015)", border:`1px solid ${color}25`,
                borderTop:"none", borderRadius:"0 0 10px 10px" }}>
                {members.map(rs => {
                  const initials  = rs.rep.name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
                  const hasCrit   = rs.flags.some(f => f.severity === "critical");
                  const hasHigh   = rs.flags.some(f => f.severity === "high");
                  const borderClr = hasCrit ? "#DC2626" : hasHigh ? "#C2610C" : rs.flags.length > 0 ? "#D97706" : "#16A34A";
                  const attColor  = rs.attPct >= 0.85 ? "#16A34A" : rs.attPct >= 0.6 ? "#D97706" : "#DC2626";
                  return (
                    <div key={rs.oid} onClick={() => { setSelected(rs.oid); setTab("overview"); }}
                      style={{ background:"#FFF", borderRadius:12,
                        border:`1px solid ${borderClr}25`,
                        borderTop:`3px solid ${borderClr}`,
                        padding:"14px 16px", cursor:"pointer", transition:"box-shadow 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.10)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>

                      {/* Rep header */}
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                        <div style={{ width:36, height:36, borderRadius:8, background:`${borderClr}18`,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:12, fontWeight:800, color:borderClr, flexShrink:0 }}>{initials}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:800, color:"#111", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{rs.rep.name}</div>
                          <div style={{ fontSize:10, color:"#888", marginTop:1 }}>{rs.rep.role || "AE"} · {rs.openCount} open deal{rs.openCount !== 1 ? "s" : ""}</div>
                        </div>
                        <div style={{ textAlign:"right", flexShrink:0 }}>
                          <div style={{ fontSize:18, fontWeight:900, color:attColor, fontFamily:"'DM Mono',monospace" }}>{(rs.attPct * 100).toFixed(0)}%</div>
                          <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase" }}>attainment</div>
                        </div>
                      </div>

                      {/* Mini stats */}
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:12,
                        padding:"8px 10px", background:"#F8F8F8", borderRadius:8 }}>
                        {[
                          { l:"Quota",    v: fmtM(rs.quota) },
                          { l:"Booked",   v: fmtM(rs.bookings), c: attColor },
                          { l:"Pipeline", v: fmtM(rs.pipeline), c: rs.covX >= 2 ? "#16A34A" : "#DC2626" },
                        ].map((s, si) => (
                          <div key={si} style={{ textAlign:"center" }}>
                            <div style={{ fontSize:9, color:"#aaa", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:2 }}>{s.l}</div>
                            <div style={{ fontSize:12, fontWeight:800, color:s.c||"#111", fontFamily:"'DM Mono',monospace" }}>{s.v||"—"}</div>
                          </div>
                        ))}
                      </div>

                      {/* Flags */}
                      {rs.flags.length === 0 ? (
                        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 8px",
                          background:"rgba(22,163,74,0.06)", borderRadius:6, border:"1px solid rgba(22,163,74,0.15)" }}>
                          <span style={{ fontSize:12 }}>✅</span>
                          <span style={{ fontSize:11, color:"#16A34A", fontWeight:600 }}>No issues flagged</span>
                        </div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          {rs.flags.slice(0,4).map((flag, fi) => (
                            <div key={fi} style={{ display:"flex", alignItems:"flex-start", gap:7,
                              padding:"6px 8px", borderRadius:6,
                              background: flag.severity==="critical"?"rgba(220,38,38,0.05)":flag.severity==="high"?"rgba(194,97,12,0.05)":"rgba(217,119,6,0.04)",
                              border:`1px solid ${sevColor(flag.severity)}20` }}>
                              <span style={{ fontSize:12, lineHeight:1.4, flexShrink:0 }}>{flag.label.split(" ")[0]}</span>
                              <span style={{ fontSize:11, color:"#444", lineHeight:1.4 }}>{flag.detail || flag.label}</span>
                            </div>
                          ))}
                          {rs.flags.length > 4 && (
                            <div style={{ fontSize:10, color:"#aaa", textAlign:"center" }}>+{rs.flags.length-4} more issues</div>
                          )}
                        </div>
                      )}

                      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:4,
                        marginTop:10, paddingTop:8, borderTop:"1px solid rgba(0,0,0,0.05)" }}>
                        <span style={{ fontSize:11, color:"#CC1A1A", fontWeight:700 }}>Open Profile</span>
                        <ChevronRight size={11} color="#CC1A1A" />
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* ── REP SELECTED: Detail panel ───────────────────────────────────── */}
      {selStats && (
      <div>
        {/* Back button */}
        <button onClick={() => setSelected(null)}
          style={{ display:"flex", alignItems:"center", gap:5, background:"none", border:"none",
            cursor:"pointer", color:"#CC1A1A", fontWeight:700, fontSize:12, marginBottom:16, padding:0 }}>
          ← Back to Team
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
            {/* Rep header */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, padding: "16px 18px", background: "#FFFFFF", borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)" }}>
              <div style={{ width: 48, height: 48, borderRadius: 11, background: `${healthColor(selStats.health)}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: healthColor(selStats.health) }}>
                {selStats.rep.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#111111", marginBottom: 2 }}>{selStats.rep.name}</div>
                <div style={{ fontSize: 11, color: "#555555" }}>{selStats.rep.segment || ""}{selStats.rep.role ? ` · ${selStats.rep.role}` : ""}{selStats.rep.startDate ? ` · Started ${selStats.rep.startDate}` : ""}</div>
              </div>
              <div style={{ textAlign: "center", padding: "8px 16px", background: `${healthColor(selStats.health)}15`, borderRadius: 8, border: `1px solid ${healthColor(selStats.health)}30` }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: healthColor(selStats.health), fontFamily: "'DM Mono',monospace", lineHeight: 1 }}>{selStats.health}</div>
                <div style={{ fontSize: 9, color: "#555555", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                  Health Score
                  <InfoIcon tip="Weighted composite of 6 signals: Quota Attainment (3x), Pipeline Coverage (2x), Win Rate (2x), Deal Activity (1x), Avg Deal Size (1x), Sales Velocity (1x). Score of 75+ is green, 50–74 is yellow, below 50 is red."/>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
              {["overview", "pipeline", "coaching", "notes", "history"].map(t => {
                const repSessions = sessions[selStats.oid] || [];
                return (
                  <button key={t} onClick={() => setTab(t)}
                    style={{ padding: "6px 14px", borderRadius: 7, border: `1px solid ${tab === t ? "rgba(204,26,26,0.10)" : "rgba(0,0,0,0.08)"}`, background: tab === t ? "rgba(204,26,26,0.10)" : "transparent", color: tab === t ? "#CC1A1A" : "#555555", fontSize: 11, fontWeight: tab === t ? 700 : 400, cursor: "pointer", textTransform: "capitalize" }}>
                    {t}
                    {t === "coaching" && selStats.flags.length > 0 && <span style={{ marginLeft: 5, fontSize: 9, background: "#DC262625", color: "#DC2626", borderRadius: 10, padding: "1px 5px", fontWeight: 800 }}>{selStats.flags.length}</span>}
                    {t === "notes" && (notes[selStats.oid] || []).length > 0 && <span style={{ marginLeft: 5, fontSize: 9, background: "rgba(204,26,26,0.10)", color: "#CC1A1A", borderRadius: 10, padding: "1px 5px", fontWeight: 700 }}>{(notes[selStats.oid] || []).length}</span>}
                    {t === "history" && repSessions.length > 0 && <span style={{ marginLeft: 5, fontSize: 9, background: "rgba(124,58,237,0.10)", color: "#7C3AED", borderRadius: 10, padding: "1px 5px", fontWeight: 700 }}>{repSessions.length}</span>}
                  </button>
                );
              })}
            </div>

            {/* Overview Tab */}
            {tab === "overview" && (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
                  {[
                    { l: "YTD Bookings",      v: fmtM(selStats.bookings),                   c: "#16A34A",  tip: "Total closed-won deal value in the current fiscal year." },
                    { l: "Quota",             v: fmtM(selStats.quota),                       c: "#111111",  tip: "Annual quota assigned to this rep in Settings → Team." },
                    { l: "Attainment",        v: `${(selStats.attPct * 100).toFixed(0)}%`,   c: healthColor(selStats.attPct >= 0.85 ? 80 : selStats.attPct >= 0.6 ? 60 : 40), tip: "YTD Bookings ÷ Annual Quota." },
                    { l: "Open Pipeline",     v: fmtM(selStats.pipeline),                    c: "#CC1A1A",  tip: "Sum of all open deal values owned by this rep." },
                    { l: "Pipeline Coverage", v: `${selStats.covX.toFixed(1)}x`,              c: selStats.covX >= 3 ? "#16A34A" : selStats.covX >= 2 ? "#D97706" : "#DC2626", tip: "Open Pipeline ÷ Annual Quota. Best practice is 3x minimum." },
                    { l: "Forecasted Close",  v: fmtM(selStats.forecastable),                c: selStats.forecastable >= selStats.remaining ? "#16A34A" : "#DC2626", tip: "Pipeline × win rate. Can this pipeline mathematically cover the remaining quota?" },
                    { l: "Win Rate",          v: `${(selStats.winRate * 100).toFixed(0)}%`,   c: "#7C3AED",  tip: "Closed-won ÷ (won + lost) deals. All-time for this rep." },
                    { l: "Avg Deal Size",     v: fmtM(selStats.avgDeal),                     c: "#C2610C",  tip: "Total YTD won value ÷ number of deals won." },
                    { l: "Avg Sales Cycle",   v: selStats.avgCycle > 0 ? `${Math.round(selStats.avgCycle)}d` : "—", c: "#111111", tip: "Average days from deal creation to close-won." },
                    { l: "New Pipe (30d)",    v: fmtM(selStats.newPipe30Val),                c: selStats.newPipe30Count > 0 ? "#16A34A" : "#DC2626", tip: "Pipeline created in the last 30 days. Leading indicator of future bookings." },
                    { l: "No Next Step",      v: selStats.noNextStepCount || "✓",            c: selStats.noNextStepCount > 0 ? "#DC2626" : "#16A34A", tip: "Open deals with no next step field filled in HubSpot." },
                    { l: "Avg Contacts/Deal", v: selStats.avgContacts != null ? selStats.avgContacts.toFixed(1) : "—", c: selStats.avgContacts != null && selStats.avgContacts < 2 ? "#D97706" : "#16A34A", tip: "Average contacts associated per open deal. Below 2 on large deals = single-thread risk." },
                  ].map((kv, i) => (
                    <div key={i} style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 8, padding: "11px 14px" }}>
                      <div style={{ fontSize: 10, color: "#555555", marginBottom: 4, display: "flex", alignItems: "center" }}>
                        {kv.l}<InfoIcon tip={kv.tip}/>
                      </div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: kv.c, fontFamily: "'DM Mono',monospace" }}>{kv.v}</div>
                    </div>
                  ))}
                </div>

                {/* Signal bars */}
                <div style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#555555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12, display: "flex", alignItems: "center", gap: 5 }}>
                    Performance Signals
                    <InfoIcon tip="Six weighted signals rolled into the Health Score. Each bar shows how this rep scores on that dimension relative to 100% (perfect). Hover each label for the calculation."/>
                  </div>
                  {COACHING_SIGNALS.map(sig => {
                    const score = selStats.scores[sig.key] || 0;
                    const pct   = Math.round(score * 100);
                    const sc    = healthColor(pct);
                    return (
                      <div key={sig.key} style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: "#111111", display: "flex", alignItems: "center" }}>
                            {sig.label}<InfoIcon tip={sig.tip}/>
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: sc, fontFamily: "'DM Mono',monospace" }}>{pct}%</span>
                        </div>
                        <div style={{ height: 5, background: "#F0F0F0", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: sc, borderRadius: 3, transition: "width 0.5s ease" }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Pipeline Tab */}
            {tab === "pipeline" && (
              <div>
                {selStats.openDeals.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "#555555", fontSize: 12 }}>No open deals</div>
                ) : (
                  <div style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 90px", padding: "8px 14px", background: "#E8E8E8", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                      {["Deal Name", "Amount", "Close Date", "Activity"].map((h, i) => (
                        <div key={i} style={{ fontSize: 10, fontWeight: 700, color: "#555555", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                      ))}
                    </div>
                    {selStats.openDeals.slice(0, 15).map((d, i) => {
                      const la = d.properties[F.lastActivity];
                      const daysSince = la ? Math.round((now - new Date(la).getTime()) / 86400000) : null;
                      const isStale   = daysSince === null || daysSince > 14;
                      const cd = d.properties[F.closedate] ? new Date(d.properties[F.closedate]).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
                      const url = dealUrl(settings.hubspot?.portalId, d.id);
                      return (
                        <div key={i} onClick={()=>url&&window.open(url,"_blank")}
                          style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 90px", padding: "9px 14px", borderBottom: i < selStats.openDeals.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none", background: i % 2 ? "rgba(0,0,0,0.01)" : "transparent", alignItems: "center", cursor: url?"pointer":"default", transition:"background 0.1s" }}
                          onMouseEnter={e=>{if(url)e.currentTarget.style.background="rgba(220,38,38,0.04)";}}
                          onMouseLeave={e=>e.currentTarget.style.background=i%2?"rgba(0,0,0,0.01)":"transparent"}>
                          <div style={{ fontSize: 12, color: "#111111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 10 }}>{d.properties[F.dealname] || "Unnamed"}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#CC1A1A", fontFamily: "'DM Mono',monospace" }}>{fmtM(d.properties[F.amount])}</div>
                          <div style={{ fontSize: 11, color: "#555555" }}>{cd}</div>
                          <div style={{ display:"flex", alignItems:"center", gap:4, fontSize: 10, color: isStale ? "#DC2626" : "#16A34A", fontWeight: 600 }}>
                            {daysSince === null ? "⚠ No activity" : isStale ? `⚠ ${daysSince}d ago` : `✓ ${daysSince}d ago`}
                            {url && <span style={{color:"#aaa"}}>↗</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Coaching Tab */}
            {tab === "coaching" && (
              <div>
                {/* Generate Insights button bar */}
                <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                  {[
                    {id:"1on1",label:"15-min 1:1",icon:"💬"},
                    {id:"quarterly",label:"Quarterly Review",icon:"📊"},
                    {id:"sixmonth",label:"6-Month Review",icon:"📈"},
                    {id:"annual",label:"Annual Review",icon:"🏆"},
                  ].map(rt => (
                    <button key={rt.id} onClick={()=>setShowReview(rt)}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"rgba(204,26,26,0.06)",border:"1px solid rgba(204,26,26,0.18)",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700,color:"#CC1A1A"}}>
                      <span>{rt.icon}</span> {rt.label}
                    </button>
                  ))}
                </div>
                {selStats.flags.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>🎯</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111111", marginBottom: 6 }}>No coaching flags right now</div>
                    <div style={{ fontSize: 11, color: "#555555" }}>This rep is tracking well across all signals.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {selStats.flags.map((flag, fi) => (
                      <div key={fi} style={{ padding: "14px 16px", background: "#FFFFFF", border: `1px solid ${sevColor(flag.severity)}25`, borderRadius: 10, borderLeft: `3px solid ${sevColor(flag.severity)}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 9, fontWeight: 800, color: sevColor(flag.severity), background: `${sevColor(flag.severity)}18`, borderRadius: 4, padding: "2px 7px" }}>
                            {flag.severity.toUpperCase()}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#111111" }}>{flag.label}</span>
                        </div>
                        <div style={{ fontSize: 11, color: "#555555", lineHeight: 1.6 }}>
                          {flag.detail || (
                            <>
                              {flag.area === "quota"      && `YTD bookings of ${fmtM(selStats.bookings)} are significantly below the ${fmtM(selStats.quota)} quota. Schedule a close-plan review and identify deals that can be accelerated this quarter.`}
                              {flag.area === "pipeline"   && `Pipeline coverage of ${selStats.covX.toFixed(1)}x is below the 3x minimum needed. Work with this rep to identify new opportunities and ensure top-of-funnel activity is sufficient.`}
                              {flag.area === "winRate"    && `Win rate of ${(selStats.winRate * 100).toFixed(0)}% is below target. Review recent lost deals together — look for patterns in objection handling, late-stage competition, or deal qualification.`}
                              {flag.area === "activity"   && `${selStats.staleCount} open deals have had no logged activity in 14+ days. This is a leading indicator of deals going cold or slipping.`}
                              {flag.area === "velocity"   && `Average sales cycle of ${Math.round(selStats.avgCycle)} days is running longer than target. Identify where deals are stalling — typically evaluation or procurement.`}
                              {flag.area === "dealSize"   && `Average deal size of ${fmtM(selStats.avgDeal)} is below target. Explore whether this rep is qualifying too narrow, discounting heavily, or avoiding enterprise conversations.`}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {tab === "notes" && (
              <div>
                {/* Coaching cadence prompts — inspired by Ambition's structured 1:1s */}
                <div style={{ marginBottom: 14, padding: "12px 14px", background: "rgba(204,26,26,0.10)", border: "1px solid rgba(204,26,26,0.10)", borderRadius: 9 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#CC1A1A", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                    1:1 Coaching Prompts
                    <InfoIcon tip="Structured coaching questions based on this rep's current performance signals. Use these to guide your next 1:1 conversation."/>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {[
                      selStats.attPct < 0.7  && "What's your close plan for this quarter — which deals are you most confident in and why?",
                      selStats.covX < 2      && "Walk me through your pipeline build strategy — what's your plan to get to 3x coverage?",
                      selStats.winRate < 0.25 && "Tell me about your last 3 losses — what patterns do you see and what would you do differently?",
                      selStats.staleCount > 1 && `You have ${selStats.staleCount} deals with no recent activity — what's the status and what are the next steps?`,
                      selStats.avgCycle > 60  && "Which deals are taking longest to close and where are they stalling most often?",
                      selStats.wonCount > 3   && `Great work on ${selStats.wonCount} wins — what's working that we should replicate across the team?`,
                      "What's your biggest obstacle right now and what do you need from me to remove it?",
                    ].filter(Boolean).slice(0, 5).map((q, qi) => (
                      <div key={qi} onClick={() => setNoteInput(q)}
                        style={{ fontSize: 11, color: "#666666", padding: "6px 10px", background: "#FFFFFF", borderRadius: 6, cursor: "pointer", lineHeight: 1.5, border: "1px solid rgba(0,0,0,0.05)" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(204,26,26,0.10)"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(0,0,0,0.05)"}>
                        💬 {q}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <input value={noteInput} onChange={e => setNoteInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveNote(selStats.oid); }}
                    placeholder="Add a coaching note, observation, or action item…"
                    style={{ flex: 1, background: "#F0F0F0", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "9px 12px", color: "#111111", fontSize: 12, outline: "none" }}
                  />
                  <button onClick={() => saveNote(selStats.oid)} disabled={!noteInput.trim()}
                    style={{ padding: "9px 16px", background: "rgba(204,26,26,0.10)", border: "1px solid rgba(204,26,26,0.10)", borderRadius: 7, color: "#CC1A1A", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    Save
                  </button>
                </div>
                {(notes[selStats.oid] || []).length === 0 ? (
                  <div style={{ textAlign: "center", padding: "30px 20px", color: "#555555", fontSize: 12 }}>No notes yet — click a prompt above or write your own.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[...(notes[selStats.oid] || [])].reverse().map((n, ni) => (
                      <div key={ni} style={{ padding: "11px 14px", background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 8 }}>
                        <div style={{ fontSize: 12, color: "#111111", lineHeight: 1.6, marginBottom: 5 }}>{n.text}</div>
                        <div style={{ fontSize: 10, color: "#555555" }}>{new Date(n.at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* History Tab */}
            {tab === "history" && (
              <HistoryTab
                repOwnerId={selStats.oid}
                repName={selStats.rep.name}
                sessions={sessions[selStats.oid] || []}
                currentMetrics={{
                  attPct: selStats.attPct, covX: selStats.covX,
                  winRate: selStats.winRate, flagCount: selStats.flags?.length || 0,
                  health: selStats.health,
                }}
                onAddNote={addSessionNote}
                onStartNew={() => { setTab("coaching"); setShowReview(true); }}
              />
            )}
          </div>
        </div>
      )}
    </div>
    {showReview && selStats && (
      <InsightsPanel
        rep={selStats.rep}
        stats={selStats}
        settings={settings}
        userId={userId}
        onClose={() => setShowReview(false)}
        onSave={(reviewType, reviewData) => saveSession(selStats.oid, reviewType, reviewData, selStats)}
      />
    )}
    </>
  );
};

// ── HistoryTab ─────────────────────────────────────────────────────────────
const METRIC_DEFS = [
  { key:"attPct",   label:"Attainment", fmt: v => `${(v*100).toFixed(0)}%`, color:"#CC1A1A",  good: v => v >= 0.85 },
  { key:"covX",     label:"Coverage",   fmt: v => `${v.toFixed(1)}x`,       color:"#D97706",  good: v => v >= 3    },
  { key:"winRate",  label:"Win Rate",   fmt: v => `${(v*100).toFixed(0)}%`, color:"#7C3AED",  good: v => v >= 0.25 },
  { key:"flagCount",label:"# Flags",    fmt: v => `${v}`,                   color:"#DC2626",  good: v => v === 0   },
];

const SparkLine = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const w = 80, h = 28;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} style={{ display:"block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts.split(" ").pop().split(",")[0]} cy={pts.split(" ").pop().split(",")[1]} r={3} fill={color} />
    </svg>
  );
};

const HistoryTab = ({ repOwnerId, repName, sessions, currentMetrics, onAddNote, onStartNew }) => {
  const [expandedId, setExpandedId] = React.useState(sessions[0]?.id || null);
  const [noteInputs, setNoteInputs] = React.useState({});

  if (sessions.length === 0) {
    return (
      <div style={{ textAlign:"center", padding:"50px 20px" }}>
        <div style={{ fontSize:28, marginBottom:12 }}>📋</div>
        <div style={{ fontSize:13, fontWeight:700, color:"#111", marginBottom:6 }}>No coaching sessions yet</div>
        <div style={{ fontSize:11, color:"#888", marginBottom:20 }}>Generate a review from the Coaching tab to start tracking {repName}'s progress.</div>
        <button onClick={onStartNew}
          style={{ padding:"9px 20px", background:"rgba(204,26,26,0.08)", border:"1px solid rgba(204,26,26,0.2)",
            borderRadius:8, color:"#CC1A1A", fontSize:12, fontWeight:700, cursor:"pointer" }}>
          + Start First Review
        </button>
      </div>
    );
  }

  // Build improvement chart data — chronological (oldest → newest)
  const chronological = [...sessions].reverse();
  const chartData = METRIC_DEFS.map(md => ({
    ...md,
    values: chronological.map(s => s.metrics?.[md.key] ?? null).filter(v => v !== null),
    dates:  chronological.map(s => new Date(s.createdAt).toLocaleDateString("en-US", { month:"short", day:"numeric" })),
  }));

  // Compute trend vs previous session
  const trend = (key) => {
    if (sessions.length < 2) return null;
    const curr = sessions[0].metrics?.[key];
    const prev = sessions[1].metrics?.[key];
    if (curr == null || prev == null) return null;
    const delta = curr - prev;
    const md = METRIC_DEFS.find(m => m.key === key);
    // For flagCount, lower is better
    const improving = key === "flagCount" ? delta < 0 : delta > 0;
    return { delta, improving, pct: prev !== 0 ? Math.abs(delta / prev * 100).toFixed(0) : null };
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Improvement tracker */}
      <div style={{ background:"#FFF", borderRadius:10, border:"1px solid rgba(0,0,0,0.08)", padding:"14px 16px" }}>
        <div style={{ fontSize:11, fontWeight:800, color:"#111", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>
          Progress Tracker — {sessions.length} session{sessions.length !== 1 ? "s" : ""}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {METRIC_DEFS.map(md => {
            const t = trend(md.key);
            const currVal = sessions[0].metrics?.[md.key];
            const isGood = currVal != null && md.good(currVal);
            return (
              <div key={md.key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em" }}>{md.label}</div>
                <div style={{ fontSize:16, fontWeight:900, color: isGood ? "#16A34A" : md.color, fontFamily:"'DM Mono',monospace" }}>
                  {currVal != null ? md.fmt(currVal) : "—"}
                </div>
                {t && (
                  <div style={{ display:"flex", alignItems:"center", gap:3, fontSize:10, fontWeight:700,
                    color: t.improving ? "#16A34A" : "#DC2626" }}>
                    {t.improving ? "▲" : "▼"} {t.pct ? `${t.pct}%` : md.fmt(Math.abs(t.delta))}
                    <span style={{ fontWeight:400, color:"#aaa" }}>vs prior</span>
                  </div>
                )}
                {sessions.length >= 2 && (
                  <SparkLine
                    data={chartData.find(c => c.key === md.key)?.values || []}
                    color={md.color}
                  />
                )}
                {sessions.length < 2 && (
                  <div style={{ fontSize:9, color:"#ccc" }}>Need 2+ sessions for trend</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Session list */}
      <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.07em" }}>
        Session History
      </div>
      {sessions.map((s, si) => {
        const isOpen = expandedId === s.id;
        const date = new Date(s.createdAt).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", year:"numeric" });
        const time = new Date(s.createdAt).toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });
        const noteVal = noteInputs[s.id] || "";
        return (
          <div key={s.id} style={{ background:"#FFF", borderRadius:10, border:"1px solid rgba(0,0,0,0.08)", overflow:"hidden" }}>
            {/* Session header */}
            <div onClick={() => setExpandedId(isOpen ? null : s.id)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", cursor:"pointer",
                background: isOpen ? "rgba(124,58,237,0.04)" : "#FFF",
                borderBottom: isOpen ? "1px solid rgba(0,0,0,0.06)" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.02)"}
              onMouseLeave={e => e.currentTarget.style.background = isOpen ? "rgba(124,58,237,0.04)" : "#FFF"}>
              <span style={{ fontSize:16, flexShrink:0 }}>{s.icon}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#111" }}>{s.label}
                  {si === 0 && <span style={{ marginLeft:7, fontSize:9, background:"rgba(124,58,237,0.10)", color:"#7C3AED", borderRadius:4, padding:"2px 6px", fontWeight:800 }}>Latest</span>}
                </div>
                <div style={{ fontSize:10, color:"#aaa", marginTop:1 }}>{date} · {time}{s.notes?.length > 0 ? ` · ${s.notes.length} note${s.notes.length !== 1 ? "s" : ""}` : ""}</div>
              </div>
              {/* Metric chips row */}
              <div style={{ display:"flex", gap:5, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end", maxWidth:420 }}>
                {[
                  s.metrics?.bookings  != null && { label:`${fmtM(s.metrics.bookings)}`,           title:"Total Bookings",    color:"#CC1A1A" },
                  s.metrics?.pipeline  != null && { label:`${fmtM(s.metrics.pipeline)} pipe`,      title:"Open Pipeline",     color:"#D97706" },
                  s.metrics?.attPct    != null && { label:`${(s.metrics.attPct*100).toFixed(0)}% att`, title:"Attainment",    color:"#CC1A1A" },
                  s.metrics?.covX      != null && { label:`${s.metrics.covX.toFixed(1)}x cov`,     title:"Pipeline Coverage", color:"#D97706" },
                  s.metrics?.activityScore != null && { label:`${s.metrics.activityScore}% act`,   title:"Activity Score",    color: s.metrics.activityScore >= 70 ? "#16A34A" : s.metrics.activityScore >= 40 ? "#D97706" : "#DC2626" },
                  s.metrics?.health    != null && { label:`${s.metrics.health} health`,             title:"CRM Health Score",  color: s.metrics.health >= 70 ? "#16A34A" : s.metrics.health >= 50 ? "#D97706" : "#DC2626" },
                  s.metrics?.flagCount > 0     && { label:`${s.metrics.flagCount} flag${s.metrics.flagCount !== 1 ? "s" : ""}`, title:"Coaching Flags", color:"#DC2626" },
                ].filter(Boolean).map((c, ci) => (
                  <span key={ci} title={c.title} style={{ fontSize:10, fontWeight:700, color:c.color,
                    background:`${c.color}12`, borderRadius:5, padding:"2px 7px", whiteSpace:"nowrap" }}>{c.label}</span>
                ))}
              </div>
              <span style={{ fontSize:14, color:"#ccc", marginLeft:4, flexShrink:0 }}>{isOpen ? "▾" : "▸"}</span>
            </div>

            {/* Expanded body */}
            {isOpen && (
              <div style={{ padding:"16px" }}>

                {/* Snapshot metrics + flags at time of session */}
                <div style={{ marginBottom:16, padding:"12px 14px", background:"#F8F8F8", borderRadius:9, border:"1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize:9, fontWeight:800, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>Snapshot at time of session</div>
                  {/* Metric row */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:8, marginBottom: s.metrics?.flags?.length > 0 ? 12 : 0 }}>
                    {[
                      { label:"Bookings",      value: s.metrics?.bookings  != null ? fmtM(s.metrics.bookings)                    : "—", color:"#CC1A1A" },
                      { label:"Pipeline",      value: s.metrics?.pipeline  != null ? fmtM(s.metrics.pipeline)                    : "—", color:"#D97706" },
                      { label:"Attainment",    value: s.metrics?.attPct    != null ? `${(s.metrics.attPct*100).toFixed(0)}%`      : "—", color: (s.metrics?.attPct||0) >= 0.85 ? "#16A34A" : (s.metrics?.attPct||0) >= 0.6 ? "#D97706" : "#DC2626" },
                      { label:"Coverage",      value: s.metrics?.covX      != null ? `${s.metrics.covX.toFixed(1)}x`              : "—", color: (s.metrics?.covX||0) >= 3 ? "#16A34A" : (s.metrics?.covX||0) >= 2 ? "#D97706" : "#DC2626" },
                      { label:"Activity",      value: s.metrics?.activityScore != null ? `${s.metrics.activityScore}%`            : "—", color: (s.metrics?.activityScore||0) >= 70 ? "#16A34A" : (s.metrics?.activityScore||0) >= 40 ? "#D97706" : "#DC2626" },
                      { label:"Health Score",  value: s.metrics?.health    != null ? `${s.metrics.health}`                        : "—", color: (s.metrics?.health||0) >= 70 ? "#16A34A" : (s.metrics?.health||0) >= 50 ? "#D97706" : "#DC2626" },
                    ].map((m, mi) => (
                      <div key={mi} style={{ textAlign:"center" }}>
                        <div style={{ fontSize:9, fontWeight:600, color:"#bbb", marginBottom:3 }}>{m.label}</div>
                        <div style={{ fontSize:13, fontWeight:900, color:m.color, fontFamily:"'DM Mono',monospace" }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  {/* Flags at time of session */}
                  {s.metrics?.flags?.length > 0 && (
                    <div>
                      <div style={{ fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:7 }}>
                        Flags at this session ({s.metrics.flags.length})
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                        {s.metrics.flags.map((f, fi) => {
                          const sevColor = f.severity === "critical" ? "#DC2626" : f.severity === "high" ? "#EA580C" : "#D97706";
                          return (
                            <div key={fi} style={{ display:"flex", alignItems:"flex-start", gap:8, padding:"7px 10px",
                              background:"#FFF", borderRadius:7, border:`1px solid ${sevColor}20`, borderLeft:`3px solid ${sevColor}` }}>
                              <span style={{ fontSize:8, fontWeight:800, color:sevColor, background:`${sevColor}15`,
                                borderRadius:3, padding:"2px 5px", flexShrink:0, marginTop:1 }}>
                                {(f.severity||"").toUpperCase()}
                              </span>
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:"#111" }}>{f.label}</div>
                                {f.detail && <div style={{ fontSize:10, color:"#777", marginTop:2, lineHeight:1.5 }}>{f.detail}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {(!s.metrics?.flags || s.metrics.flags.length === 0) && (
                    <div style={{ fontSize:11, color:"#16A34A", fontWeight:600 }}>✓ No coaching flags at time of session</div>
                  )}
                </div>

                {/* Review content */}
                <SessionReviewRenderer type={s.type} review={s.review} />
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>Session Notes</div>
                  {(s.notes || []).length > 0 && (
                    <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:10 }}>
                      {s.notes.map((n, ni) => (
                        <div key={ni} style={{ padding:"9px 12px", background:"#F8F8F8", borderRadius:7, border:"1px solid rgba(0,0,0,0.06)" }}>
                          <div style={{ fontSize:12, color:"#111", lineHeight:1.5 }}>{n.text}</div>
                          <div style={{ fontSize:10, color:"#bbb", marginTop:4 }}>
                            {new Date(n.at).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <input
                      value={noteVal}
                      onChange={e => setNoteInputs(p => ({ ...p, [s.id]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === "Enter" && noteVal.trim()) {
                          onAddNote(repOwnerId, s.id, noteVal);
                          setNoteInputs(p => ({ ...p, [s.id]: "" }));
                        }
                      }}
                      placeholder="Add a note to this session…"
                      style={{ flex:1, background:"#F0F0F0", border:"1px solid rgba(0,0,0,0.08)", borderRadius:7,
                        padding:"8px 12px", fontSize:12, color:"#111", outline:"none" }}
                    />
                    <button
                      onClick={() => {
                        if (noteVal.trim()) {
                          onAddNote(repOwnerId, s.id, noteVal);
                          setNoteInputs(p => ({ ...p, [s.id]: "" }));
                        }
                      }}
                      disabled={!noteVal.trim()}
                      style={{ padding:"8px 14px", background:"rgba(124,58,237,0.08)", border:"1px solid rgba(124,58,237,0.2)",
                        borderRadius:7, color:"#7C3AED", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Renders a saved review inline (same layout as InsightsPanel but compact, read-only)
const SessionReviewRenderer = ({ type, review }) => {
  if (!review) return <div style={{ fontSize:12, color:"#aaa" }}>No review data</div>;
  if (type === "1on1") return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {review.agenda?.length > 0 && (
        <Section title="📋 Agenda">
          {review.agenda.map((a,i) => (
            <div key={i} style={{ display:"flex", gap:10, padding:"6px 0", borderBottom:"1px solid rgba(0,0,0,0.04)" }}>
              <span style={{ fontSize:10, fontWeight:800, color:"#CC1A1A", fontFamily:"'DM Mono',monospace", flexShrink:0, minWidth:32 }}>{a.time}</span>
              <div><div style={{ fontSize:11, fontWeight:700, color:"#111" }}>{a.topic}</div><div style={{ fontSize:10, color:"#666" }}>{a.notes}</div></div>
            </div>
          ))}
        </Section>
      )}
      {review.top_questions?.length > 0 && <Section title="❓ Questions"><BulletList items={review.top_questions} /></Section>}
      {review.close_with && <Section title="✅ Close with"><p style={{ fontSize:11, color:"#333", lineHeight:1.5 }}>{review.close_with}</p></Section>}
    </div>
  );
  if (type === "quarterly") return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {review.summary && <p style={{ fontSize:12, color:"#333", lineHeight:1.6 }}>{review.summary}</p>}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {review.strengths?.length > 0 && <Section title="💪 Strengths"><BulletList items={review.strengths} color="#16A34A" /></Section>}
        {review.areas_to_improve?.length > 0 && <Section title="🎯 Improve"><BulletList items={review.areas_to_improve} color="#DC2626" /></Section>}
      </div>
    </div>
  );
  if (type === "sixmonth") return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {review.summary && <p style={{ fontSize:12, color:"#333", lineHeight:1.6 }}>{review.summary}</p>}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {review.h1_performance?.wins?.length > 0 && <Section title="H1 Wins"><BulletList items={review.h1_performance.wins} color="#16A34A" /></Section>}
        {review.h1_performance?.misses?.length > 0 && <Section title="H1 Misses"><BulletList items={review.h1_performance.misses} color="#DC2626" /></Section>}
      </div>
    </div>
  );
  if (type === "annual") return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {review.rating && (
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"8px 14px",
          background: review.rating==="Exceeds"?"rgba(22,163,74,0.08)":review.rating==="Meets"?"rgba(217,119,6,0.08)":"rgba(220,38,38,0.08)",
          borderRadius:8 }}>
          <span style={{ fontSize:14, fontWeight:900, color: review.rating==="Exceeds"?"#16A34A":review.rating==="Meets"?"#D97706":"#DC2626" }}>{review.rating}</span>
          <span style={{ fontSize:11, color:"#555" }}>Overall rating</span>
        </div>
      )}
      {review.narrative && <p style={{ fontSize:12, color:"#333", lineHeight:1.6 }}>{review.narrative}</p>}
    </div>
  );
  return <pre style={{ fontSize:10, color:"#aaa", overflow:"auto" }}>{JSON.stringify(review, null, 2)}</pre>;
};

// ── InsightsPanel ──────────────────────────────────────────────────────────
const REVIEW_PROMPTS = {
  "1on1": (rep, s) => `You are a senior sales manager preparing for a 15-minute 1:1 with ${rep.name}.

Rep data:
- Quota: ${fmtM(s.quota)} | YTD Booked: ${fmtM(s.bookings)} | Attainment: ${(s.attPct*100).toFixed(0)}%
- Open Pipeline: ${fmtM(s.pipeline)} | Coverage: ${s.covX.toFixed(1)}x
- Win Rate: ${(s.winRate*100).toFixed(0)}% | Avg Deal: ${fmtM(s.avgDeal)} | Avg Cycle: ${Math.round(s.avgCycle)}d
- Open deals: ${s.openCount} | New pipe (30d): ${fmtM(s.newPipe30Val||0)}
- Flags: ${s.flags.map(f=>`${f.severity.toUpperCase()}: ${f.label}`).join("; ")||"None"}

Generate a 15-minute 1:1 coaching brief. Use this exact JSON structure:
{"agenda":[{"time":"0:00","topic":"string","notes":"string"},...],"top_questions":["string",...],"deal_to_review":{"reason":"string","talking_points":["string",...]},"close_with":"string"}

Return ONLY the JSON object, no markdown, no explanation.`,

  "quarterly": (rep, s) => `You are a sales director preparing a quarterly review for ${rep.name}.

Rep data:
- Quota: ${fmtM(s.quota)} | YTD Booked: ${fmtM(s.bookings)} | Attainment: ${(s.attPct*100).toFixed(0)}%
- Win Rate: ${(s.winRate*100).toFixed(0)}% | Avg Deal: ${fmtM(s.avgDeal)} | Pipeline: ${fmtM(s.pipeline)} (${s.covX.toFixed(1)}x)
- Flags: ${s.flags.map(f=>`${f.severity.toUpperCase()}: ${f.label}`).join("; ")||"None"}

Generate a quarterly performance review. Use this exact JSON:
{"summary":"string","strengths":["string",...],"areas_to_improve":["string",...],"actions":[{"owner":"Rep|Manager","action":"string","by":"string"},...],"outlook":"string"}

Return ONLY the JSON object, no markdown, no explanation.`,

  "sixmonth": (rep, s) => `You are a VP of Sales preparing a 6-month review for ${rep.name}.

Rep data:
- Quota: ${fmtM(s.quota)} | YTD Booked: ${fmtM(s.bookings)} | Attainment: ${(s.attPct*100).toFixed(0)}%
- Win Rate: ${(s.winRate*100).toFixed(0)}% | Avg Deal: ${fmtM(s.avgDeal)} | Pipeline: ${fmtM(s.pipeline)} (${s.covX.toFixed(1)}x)
- Flags: ${s.flags.map(f=>`${f.severity.toUpperCase()}: ${f.label}`).join("; ")||"None"}

Generate a 6-month review. Use this exact JSON:
{"summary":"string","h1_performance":{"wins":["string",...],"misses":["string",...]},"h2_plan":{"goals":["string",...],"focus_areas":["string",...]},"development":{"skills_to_build":["string",...],"resources":["string",...]},"manager_notes":"string"}

Return ONLY the JSON object, no markdown, no explanation.`,

  "annual": (rep, s) => `You are a Chief Revenue Officer preparing an annual review for ${rep.name}.

Rep data:
- Quota: ${fmtM(s.quota)} | YTD Booked: ${fmtM(s.bookings)} | Attainment: ${(s.attPct*100).toFixed(0)}%
- Win Rate: ${(s.winRate*100).toFixed(0)}% | Avg Deal: ${fmtM(s.avgDeal)} | Avg Cycle: ${Math.round(s.avgCycle)}d
- Flags: ${s.flags.map(f=>`${f.severity.toUpperCase()}: ${f.label}`).join("; ")||"None"}

Generate an annual performance review. Use this exact JSON:
{"rating":"Exceeds|Meets|Below","narrative":"string","top_achievements":["string",...],"development_areas":["string",...],"next_year_plan":{"quota_recommendation":"string","focus":["string",...]},"compensation_note":"string"}

Return ONLY the JSON object, no markdown, no explanation.`,
};

const InsightsPanel = ({ rep, stats, settings, onClose, onSave, userId }) => {
  const [reviewType, setReviewType] = React.useState(null);
  const [loading, setLoading]       = React.useState(false);
  const [result, setResult]         = React.useState(null);
  const [error, setError]           = React.useState(null);

  const generate = async (rt) => {
    setReviewType(rt);
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const prompt = REVIEW_PROMPTS[rt.id]?.(rep, stats);
      if (!prompt) throw new Error("Unknown review type");
      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model:      "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages:   [{ role: "user", content: prompt }],
          // Usage tracking fields — passed to Supabase via proxy
          portal_id:  settings?.hubspot?.portalId || "unknown",
          user_id:    userId || null,
          org_id:     "00000000-0000-0000-0000-000000000001",
          feature:    `coaching_${rt.id}`,
        }),
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("") || "";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      // Auto-save the session with a metric snapshot
      if (onSave) onSave(rt, parsed);
    } catch (e) {
      setError("Failed to generate review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Renderers per type ──
  const render1on1 = r => (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <Section title="📋 Agenda">
        {r.agenda?.map((a,i)=>(
          <div key={i} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
            <span style={{fontSize:11,fontWeight:800,color:"#CC1A1A",fontFamily:"'DM Mono',monospace",flexShrink:0,minWidth:36}}>{a.time}</span>
            <div><div style={{fontSize:12,fontWeight:700,color:"#111"}}>{a.topic}</div><div style={{fontSize:11,color:"#666",marginTop:2}}>{a.notes}</div></div>
          </div>
        ))}
      </Section>
      <Section title="❓ Questions to ask">
        <BulletList items={r.top_questions} />
      </Section>
      {r.deal_to_review && (
        <Section title="🔍 Deal to spotlight">
          <div style={{fontSize:11,color:"#555",marginBottom:8}}>{r.deal_to_review.reason}</div>
          <BulletList items={r.deal_to_review.talking_points} />
        </Section>
      )}
      {r.close_with && <Section title="✅ Close with"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.close_with}</p></Section>}
    </div>
  );

  const renderQuarterly = r => (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {r.summary && <Section title="📊 Summary"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.summary}</p></Section>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Section title="💪 Strengths"><BulletList items={r.strengths} color="#16A34A"/></Section>
        <Section title="🎯 Areas to improve"><BulletList items={r.areas_to_improve} color="#DC2626"/></Section>
      </div>
      <Section title="📌 Action items">
        {r.actions?.map((a,i)=>(
          <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
            <span style={{fontSize:9,fontWeight:800,color:"#fff",background:a.owner==="Rep"?"#CC1A1A":"#7C3AED",borderRadius:4,padding:"2px 6px",flexShrink:0,marginTop:2}}>{a.owner}</span>
            <div style={{flex:1}}><span style={{fontSize:12,color:"#111"}}>{a.action}</span><span style={{fontSize:10,color:"#aaa",marginLeft:8}}>by {a.by}</span></div>
          </div>
        ))}
      </Section>
      {r.outlook && <Section title="🔭 Outlook"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.outlook}</p></Section>}
    </div>
  );

  const renderSixMonth = r => (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {r.summary && <Section title="📈 Overview"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.summary}</p></Section>}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Section title="H1 Wins"><BulletList items={r.h1_performance?.wins} color="#16A34A"/></Section>
        <Section title="H1 Misses"><BulletList items={r.h1_performance?.misses} color="#DC2626"/></Section>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Section title="H2 Goals"><BulletList items={r.h2_plan?.goals}/></Section>
        <Section title="H2 Focus Areas"><BulletList items={r.h2_plan?.focus_areas}/></Section>
      </div>
      <Section title="🧠 Development">
        <BulletList items={r.development?.skills_to_build} />
      </Section>
      {r.manager_notes && <Section title="📝 Manager notes"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.manager_notes}</p></Section>}
    </div>
  );

  const renderAnnual = r => {
    const ratingColor = r.rating==="Exceeds"?"#16A34A":r.rating==="Meets"?"#D97706":"#DC2626";
    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:`${ratingColor}0D`,border:`1px solid ${ratingColor}30`,borderRadius:10}}>
          <span style={{fontSize:22,fontWeight:900,color:ratingColor}}>{r.rating}</span>
          <span style={{fontSize:11,color:"#555"}}>Overall performance rating</span>
        </div>
        {r.narrative && <Section title="🏆 Narrative"><p style={{fontSize:12,color:"#333",lineHeight:1.6}}>{r.narrative}</p></Section>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <Section title="Top Achievements"><BulletList items={r.top_achievements} color="#16A34A"/></Section>
          <Section title="Development Areas"><BulletList items={r.development_areas} color="#DC2626"/></Section>
        </div>
        {r.next_year_plan && (
          <Section title="📅 Next Year Plan">
            <div style={{fontSize:12,fontWeight:700,color:"#111",marginBottom:8}}>Quota recommendation: {r.next_year_plan.quota_recommendation}</div>
            <BulletList items={r.next_year_plan.focus} />
          </Section>
        )}
        {r.compensation_note && <Section title="💰 Compensation note"><p style={{fontSize:12,color:"#555",lineHeight:1.6}}>{r.compensation_note}</p></Section>}
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    if (reviewType?.id === "1on1")      return render1on1(result);
    if (reviewType?.id === "quarterly") return renderQuarterly(result);
    if (reviewType?.id === "sixmonth")  return renderSixMonth(result);
    if (reviewType?.id === "annual")    return renderAnnual(result);
    return null;
  };

  const REVIEW_TYPES = [
    {id:"1on1",label:"15-min 1:1",icon:"💬"},
    {id:"quarterly",label:"Quarterly Review",icon:"📊"},
    {id:"sixmonth",label:"6-Month Review",icon:"📈"},
    {id:"annual",label:"Annual Review",icon:"🏆"},
  ];

  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"flex-start",justifyContent:"flex-end"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:520,height:"100%",background:"#F8F8F8",boxShadow:"-8px 0 40px rgba(0,0,0,0.18)",display:"flex",flexDirection:"column",overflowY:"auto"}}>

        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:"1px solid rgba(0,0,0,0.08)",background:"#FFF",position:"sticky",top:0,zIndex:10}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <div style={{fontSize:14,fontWeight:800,color:"#111"}}>AI Coaching Brief</div>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#aaa",lineHeight:1}}>✕</button>
          </div>
          <div style={{fontSize:11,color:"#888"}}>{rep.name} · Select a review type to generate</div>
          {/* Type selector */}
          <div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
            {REVIEW_TYPES.map(rt=>(
              <button key={rt.id} onClick={()=>generate(rt)}
                style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",
                  background:reviewType?.id===rt.id?"rgba(204,26,26,0.10)":"rgba(0,0,0,0.04)",
                  border:reviewType?.id===rt.id?"1px solid rgba(204,26,26,0.3)":"1px solid transparent",
                  borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:700,
                  color:reviewType?.id===rt.id?"#CC1A1A":"#555"}}>
                <span>{rt.icon}</span>{rt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,padding:"20px 22px"}}>
          {!reviewType && (
            <div style={{textAlign:"center",padding:"60px 20px",color:"#aaa"}}>
              <div style={{fontSize:28,marginBottom:12}}>✨</div>
              <div style={{fontSize:13,fontWeight:600,color:"#555"}}>Select a review type above</div>
              <div style={{fontSize:11,marginTop:6}}>Generating a structured coaching brief based on {rep.name}'s live data.</div>
            </div>
          )}
          {loading && (
            <div style={{textAlign:"center",padding:"60px 20px"}}>
              <div style={{fontSize:28,marginBottom:12,animation:"spin 1s linear infinite"}}>⚙️</div>
              <div style={{fontSize:13,fontWeight:600,color:"#555"}}>Generating {reviewType?.label}…</div>
              <div style={{fontSize:11,color:"#aaa",marginTop:6}}>Analyzing {rep.name}'s data</div>
            </div>
          )}
          {error && (
            <div style={{padding:"16px",background:"rgba(220,38,38,0.06)",border:"1px solid rgba(220,38,38,0.2)",borderRadius:10,color:"#DC2626",fontSize:12}}>{error}</div>
          )}
          {result && !loading && renderResult()}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components used by InsightsPanel ──
const Section = ({title, children}) => (
  <div style={{background:"#FFF",borderRadius:10,border:"1px solid rgba(0,0,0,0.07)",padding:"14px 16px"}}>
    <div style={{fontSize:11,fontWeight:800,color:"#111",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>{title}</div>
    {children}
  </div>
);
const BulletList = ({items, color="#555"}) => (
  <ul style={{margin:0,padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:5}}>
    {(items||[]).map((item,i)=>(
      <li key={i} style={{display:"flex",alignItems:"flex-start",gap:7,fontSize:12,color:"#333",lineHeight:1.5}}>
        <span style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0,marginTop:5}}/>
        {item}
      </li>
    ))}
  </ul>
);
export default RepCoaching;
